/**
 * useAppPlusScoring — loads the ACO's submissions + config for the viewing
 * year, runs the YEAR'S logic module CLIENT-SIDE (D13: all business
 * calculations resolve through logicForYear — switching the viewing year
 * switches the rules with the data), and exposes an ephemeral what-if +
 * explicit save.
 *
 * The pure functions live in src/lib/app-plus/years/<year>/ and are
 * re-exported here for compatibility (the 2026 test suite pins them —
 * FROZEN fixtures, never edited for a later year).
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { AppPlusDataService } from '@/lib/app-plus-data-service';
import type { AcoScoringConfig, AppQualityScore } from '@/lib/app-plus-scoring';
import { logicForYear } from '@/lib/app-plus/years/registry';
import type {
  StoredSubmission, CalculatedCounts, CalculatedCountsByType, EngineInputs, SimulateScenario,
} from '@/lib/app-plus/years/types';
import { qk, keepPreviousSameEntity } from '@/lib/query-keys';
import { useAcoGoals, usePerformanceSummaries } from '@/hooks/queries/useMipsQueries';

// Compatibility re-exports (2026 tests + existing imports; new code should
// resolve through logicForYear).
export {
  CLINICAL_IDS, decodeSubmissions, configFromRow, applyCalculatedCounts, runEngine,
  recordLane, switchLane, laneStatus,
} from '@/lib/app-plus/years/2026';
export type { StoredSubmission, CalculatedCounts, CalculatedCountsByType, EngineInputs, SimulateScenario };

// ===== The React hook =====

export interface UseAppPlusScoring {
  loading: boolean;
  yearSupported: boolean;
  /** Set when reference data fails to load — module renders a config-error state (EM L2). */
  configError: string | null;
  configRow: Record<string, unknown> | null;
  engineConfig: AcoScoringConfig;
  /** Persisted rows keyed by measure_id (as stored — manual counts). */
  entries: Record<string, StoredSubmission>;
  /** Rows with calc-mode counts substituted from the pipeline (what the engine consumes). */
  effectiveEntries: Record<string, StoredSubmission>;
  /** Pipeline counts keyed [base measureId][collection type] (R14: one lane
   *  per collection type; Medicare CQM streams re-keyed from SSP twin ids).
   *  Empty until the ACO has calculated data. */
  calculated: CalculatedCountsByType;
  /** Score computed from PERSISTED rows. */
  applied: AppQualityScore | null;
  /** Pure what-if: overrides merged over persisted rows (+ optional toolbar scenario). NEVER persisted. */
  simulate: (overrides: Partial<Record<string, Partial<StoredSubmission>>>, scenario?: SimulateScenario) => AppQualityScore | null;
  /** Persist changed rows + the recomputed score. Throws when the year is locked (caller gates too). */
  save: (changed: StoredSubmission[]) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAppPlusScoring(acoId: number | null, shadowPracticeId?: number | null): UseAppPlusScoring {
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const queryClient = useQueryClient();
  const [configError, setConfigError] = useState<string | null>(null);

  const svc = AppPlusDataService.getInstance();
  const yearSupported = svc.getSupportedYears().includes(programYear);
  // D13: the year's logic module — rules switch with the viewing year.
  const logic = useMemo(() => (yearSupported ? logicForYear(programYear) : null), [programYear, yearSupported]);

  // [Snappiness Phase 3] Shared React Query cache: every consumer of this hook
  // (Overview, Configuration, QPS, InsightsRoster, RosterExposureCard) reads
  // the SAME three cache entries per ACO/year — navigating between APP Plus
  // pages no longer refires the trio. The shadow-practice summaries share the
  // MIPS summaries key (cross-program dedupe with PracticeOverview etc.).
  const enabled = !!acoId && yearSupported;
  const submissionsKey = qk.acoSubmissions(acoId ?? '', programYear);

  // Entity-guarded hold: year switches for the SAME ACO keep last-good data
  // visible (no skeleton); switching ACO must not paint the previous ACO's
  // numbers. Key: ['aco', acoId, ..., year] — entity at index 1.
  const subsQ = useQuery<{ submissions?: StoredSubmission[] } | null>({
    queryKey: submissionsKey,
    queryFn: () => apiClient.get(`/api/acos/${acoId}/submissions?year=${programYear}`),
    enabled,
    placeholderData: keepPreviousSameEntity(1, String(acoId ?? '')),
  });
  const cfgQ = useQuery<Record<string, unknown> | null>({
    queryKey: qk.acoConfig(acoId ?? '', programYear),
    queryFn: () => apiClient.get(`/api/acos/${acoId}/config?year=${programYear}`),
    enabled,
    placeholderData: keepPreviousSameEntity<Record<string, unknown> | null>(1, String(acoId ?? '')),
  });
  // D12: pipeline counts for the ACO's shadow practice (GROUP evaluation).
  // Fail-soft: query error / no shadow yet → calculated stays {}, manual only.
  const summariesQ = usePerformanceSummaries(
    shadowPracticeId ?? null, programYear, 'GROUP', null, enabled);

  // Prefetch Actions goals in parallel with scoring. QPS used to mount
  // AcoActionsCard only AFTER scoring.loading cleared, so the tiny goals GET
  // started last and the card visibly "popped in" after the heavy table.
  useAcoGoals(enabled ? acoId : null, programYear);

  const entries = useMemo<Record<string, StoredSubmission>>(() => {
    const rows: StoredSubmission[] = subsQ.data?.submissions ?? [];
    return Object.fromEntries(rows.map((r: StoredSubmission) => [r.measure_id, r]));
  }, [subsQ.data]);

  const configRow = cfgQ.data ?? null;

  const calculated = useMemo<CalculatedCountsByType>(() => {
    if (!logic || !shadowPracticeId || !Array.isArray(summariesQ.data)) return {};
    // R14 Phase E: one lane per (base measureId, collection type).
    // Medicare CQM outcomes arrive under the SSP twin ids (001SSP…) —
    // re-key them to the base id via the measure set's sspVariantId
    // mapping (the SAME mapping benchmark resolution uses; never fork it).
    const sspToBase: Record<string, string> = {};
    for (const m of logic.getMeasureSet(programYear).measures) {
      if (m.sspVariantId) sspToBase[m.sspVariantId] = m.measureId;
    }
    const byMeasure: CalculatedCountsByType = {};
    for (const s of summariesQ.data) {
      if (!s.counts) continue;
      const baseId = sspToBase[s.measureId] ?? s.measureId;
      if (!logic.clinicalMeasureIds.includes(baseId)) continue;
      const lane = s.submissionMethod || 'MIPS CQM';
      byMeasure[baseId] ??= {};
      if (!byMeasure[baseId][lane]) {
        byMeasure[baseId][lane] = {
          ...(s.counts as CalculatedCounts),
          submissionMethod: lane,
          pipelineMeasureId: s.measureId,
        };
      }
    }
    return byMeasure;
  }, [logic, shadowPracticeId, summariesQ.data, programYear]);

  const loading =
    enabled &&
    (subsQ.isPending || cfgQ.isPending || (!!shadowPracticeId && summariesQ.isPending));

  const refresh = useCallback(async () => {
    if (!acoId || !yearSupported) return;
    await Promise.all([
      subsQ.refetch(),
      cfgQ.refetch(),
      shadowPracticeId ? summariesQ.refetch() : Promise.resolve(null),
    ]);
    setConfigError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acoId, yearSupported, shadowPracticeId, subsQ.refetch, cfgQ.refetch, summariesQ.refetch]);

  const engineConfig = useMemo(
    () => (logic ?? logicForYear(2026)).configFromRow(configRow), [logic, configRow]);

  // The engine consumes EFFECTIVE rows: calc-mode measures carry pipeline counts.
  const effectiveEntries = useMemo(
    () => (logic ? logic.applyCalculatedCounts(entries, calculated) : entries),
    [logic, entries, calculated]);

  const applied = useMemo(() => {
    if (!yearSupported || !logic) return null;
    try {
      return logic.runEngine(Object.values(effectiveEntries), engineConfig, programYear);
    } catch (e) {
      // e.g. getCahpsSsms throws on a bad benchmark-file edit — render the
      // error state, never crash the module (EM L2).
      setConfigError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [logic, effectiveEntries, engineConfig, programYear, yearSupported]);

  const simulate = useCallback((overrides: Partial<Record<string, Partial<StoredSubmission>>>, scenario?: SimulateScenario) => {
    if (!yearSupported || !logic) return null;
    const merged = { ...entries };
    for (const [id, patch] of Object.entries(overrides)) {
      if (!patch) continue;
      merged[id] = { ...(merged[id] ?? { measure_id: id, collection_type: 'MIPS CQM', reported: true }), ...patch };
    }
    try {
      return logic.runEngine(Object.values(logic.applyCalculatedCounts(merged, calculated)), engineConfig, programYear, scenario);
    } catch {
      return null;
    }
  }, [logic, entries, calculated, engineConfig, programYear, yearSupported]);

  const save = useCallback(async (changed: StoredSubmission[]) => {
    if (!acoId) throw new Error('No ACO selected');
    if (!logic) throw new Error(`Program year ${programYear} is not supported`);
    if (isCurrentYearLocked) throw new Error(`Program year ${programYear} is locked (read-only)`);
    for (const row of changed) {
      await apiClient.put(`/api/acos/${acoId}/submissions/${row.measure_id}?year=${programYear}`, row);
    }
    // Recompute on the post-save state (with calc-mode counts) and persist the score.
    const merged = { ...entries, ...Object.fromEntries(changed.map(r => [r.measure_id, r])) };
    const score = logic.runEngine(Object.values(logic.applyCalculatedCounts(merged, calculated)), engineConfig, programYear);
    await apiClient.put(`/api/acos/${acoId}/scores?year=${programYear}`, { result: score });
    // Write the merged rows into the shared cache so every consumer of this
    // hook sees the save immediately (no refetch round trip).
    queryClient.setQueryData(submissionsKey, (old: { submissions?: StoredSubmission[] } | null | undefined) => ({
      ...(old ?? {}),
      submissions: Object.values(merged),
    }));
  }, [acoId, logic, isCurrentYearLocked, programYear, entries, calculated, engineConfig, queryClient, submissionsKey]);

  return {
    // `loading` is already gated on (acoId && yearSupported): no ACO or an
    // unsupported year reports loading=false synchronously, so pages render
    // their empty state instead of a skeleton. With keepPreviousData, year
    // switches keep last-good data visible while revalidating (no skeleton).
    loading,
    yearSupported, configError, configRow, engineConfig,
    entries, effectiveEntries, calculated, applied, simulate, save, refresh,
  };
}
